"use client";

import {
  useState,
  type FC,
  type ReactNode,
  useMemo,
  useEffect,
  useRef,
  Fragment,
  type KeyboardEventHandler,
} from "react";
import { isArray, mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import { Text } from "../Text";
import { Flex } from "../Flex";
import { ScrollPane } from "../ScrollPane";
import { TextField } from "../TextField";
import { Ruler } from "../Ruler/Ruler";
import { Icon } from "../Icon";
import { SelectListItem } from "./components/SelectListItem";

import {
  containerStyle,
  menuStyle,
  optionLabelStyle,
  scrollStyle,
  noResultsStyle,
  searchContainerStyle,
  scrollContentStyle,
} from "./SelectList.css";

export type SelectListSelectHandler = (
  selectedOption: SelectListOption,
) => void;

export type SelectListValue = string | boolean | number | (string | number)[];

export type SelectListOption = {
  value: SelectListValue;
  label: ReactNode;
  trailing?: ReactNode;
  leading?: ReactNode;
  disabled?: boolean;
  variant?: "default" | "error";
  ruler?: boolean;
};

export type SelectListProps = {
  options: SelectListOption[];
  value?: SelectListValue;
  search?: boolean;
  multi?: boolean;
  onSelect: SelectListSelectHandler;
  label?: string;
  description?: string;
  id?: string;
  defaultHighlightedIndex?: number;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

/**
 * SelectList component is a dropdown component that allows users to select
 * a single option from a list of options.
 */
export const SelectList: FC<SelectListProps> = ({
  options,
  value,
  search = false,
  multi = false,
  onSelect,
  label,
  description,
  id = "select-list",
  defaultHighlightedIndex = 0,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
}) => {
  const { t } = useTranslation();

  const [highlightedIndex, setHighlightedIndex] = useState(
    defaultHighlightedIndex,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightedIndex === -1 && defaultHighlightedIndex === 0) {
      setHighlightedIndex(0);
    }
  }, [highlightedIndex, defaultHighlightedIndex]);

  // Filter options
  // ------------------------------

  const filteredOptions = useMemo(() => {
    if (!searchQuery) {
      return options;
    }

    return options.filter((option) =>
      `${option?.label ?? ""} ${option?.value ?? ""}`
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  // Handlers
  // ------------------------------

  const handleOptionSelect = (option: SelectListOption) => {
    onSelect(option);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev === filteredOptions.length - 1 ? 0 : prev + 1,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev === 0 ? filteredOptions.length - 1 : prev - 1,
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (
          filteredOptions &&
          highlightedIndex !== -1 &&
          filteredOptions[highlightedIndex]
        ) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
    }
  };

  const handleSearchKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev === filteredOptions.length - 1 ? 0 : prev + 1,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev === 0 ? filteredOptions.length - 1 : prev - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (
          filteredOptions &&
          highlightedIndex !== -1 &&
          filteredOptions[highlightedIndex]
        ) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
    }
  };

  // Scroll to selected option
  // ------------------------------

  const scrollRef = useRef<HTMLDivElement>(null);
  const firstScrollTo = useRef(true);

  const scrollToSelected = (
    container: HTMLDivElement | null,
    selector: string,
  ) => {
    if (!container) {
      return;
    }

    const selectedElement = container.querySelector(selector);
    if (!selectedElement) {
      return;
    }

    const elementRect = selectedElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const scrollPosition =
      container.scrollTop +
      selectedElement.getBoundingClientRect().top -
      container.getBoundingClientRect().top -
      containerRect.height / 2 +
      elementRect.height / 2;

    container.scrollTo({
      top: scrollPosition,
      behavior: firstScrollTo.current ? "instant" : "smooth",
    });

    firstScrollTo.current = false;
  };

  useEffect(() => {
    if (!value) {
      return;
    }
    setHighlightedIndex(
      filteredOptions.findIndex((option) => option.value === value),
    );
    scrollToSelected(scrollRef.current, `[data-value="${value?.toString()}"]`);
  }, [value, filteredOptions]);

  useEffect(() => {
    if (!highlightedIndex) {
      return;
    }
    const value = filteredOptions?.[highlightedIndex]?.value;
    if (!value) {
      return;
    }
    scrollToSelected(scrollRef.current, `[data-value="${value?.toString()}"]`);
  }, [highlightedIndex]);

  return (
    <div
      ref={containerRef}
      className={containerStyle()}
      role="listbox"
      id={id}
      aria-label={ariaLabel || label}
      aria-labelledby={ariaLabelledby}
      aria-activedescendant={filteredOptions[
        highlightedIndex
      ]?.value?.toString()}
      aria-multiselectable={multi}
      aria-busy={search && searchQuery.length > 0}
      aria-live="polite"
      tabIndex={-1}
    >
      {description && (
        <div id={`${id}-description`} className="sr-only">
          {description}
        </div>
      )}
      <Flex gap="xxs2" direction="column">
        {search && (
          <Flex className={searchContainerStyle} direction="column" gap="xs">
            <TextField
              placeholder={t("general.search")}
              variant="opaque"
              size="minimal"
              value={searchQuery}
              onChange={setSearchQuery}
              trailing={<Icon icon="search" size="s" color="placeholder" />}
              autoFocus
              onKeyDown={handleSearchKeyDown}
              aria-controls={id}
              aria-label={t("general.search")}
            />
            <Ruler />
          </Flex>
        )}

        <ScrollPane
          ref={scrollRef}
          className={mergeClasses(scrollStyle)}
          contentClassName={scrollContentStyle}
          overlapColor="background"
          overlapSize="s"
          tabIndex={0}
          innerTabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <Flex as="ul" direction="column" className={menuStyle()}>
            {filteredOptions?.map((option, index) => {
              let active = false;
              if (multi) {
                active =
                  isArray(value) &&
                  (value.includes(option.value as string | number) ||
                    value.map(String).includes(option.value.toString()));
              } else {
                active = value?.toString() === option.value?.toString();
              }

              return (
                <Fragment key={`${index}-${option.value}`}>
                  {option.ruler && <Ruler />}

                  <SelectListItem
                    value={option.value}
                    active={active}
                    selected={highlightedIndex === index}
                    onSelect={() => handleOptionSelect(option)}
                    multi={multi}
                    leading={option.leading}
                    disabled={option.disabled}
                    variant={option.variant}
                  >
                    <Text clamp={2} className={optionLabelStyle()}>
                      {option.label}
                    </Text>
                  </SelectListItem>
                </Fragment>
              );
            })}

            {filteredOptions.length === 0 && (
              <Text variant="bodyM" className={noResultsStyle()}>
                {t("general.noResults")}
              </Text>
            )}
          </Flex>
        </ScrollPane>
      </Flex>
    </div>
  );
};
